/**
 * <living-room-upsell-v2> custom element for the V2 PDP "Complete your
 * living room set up" panel and its matching drawer (both
 * snippets/living-room-upsell-v2.liquid and
 * snippets/living-room-drawer-v2.liquid render a <living-room-upsell-v2>
 * wrapper — see this file's connectedCallback, which scopes every query
 * to `this` so both instances work independently on the same page).
 *
 * Two responsibilities, both scoped per-instance:
 *
 * 1. Long / Square shape toggle (data-lru-shape-input radios) — on
 *    change, swaps the sibling card's data-lru-price display to the
 *    newly selected variant's combined price (data-combined-price-cents,
 *    the same tray+discount math as the card's SSR price — NOT the bare
 *    variant price) / compare-at (data-compare-cents, mirroring the
 *    exact three-way SSR strikethrough branch for that variant), both
 *    integer cents per this theme's Liquid/JSON convention — see
 *    assets/component-configuration-picker-v2.js's doc comment — formatted
 *    via the same 3-tier money formatter duplicated from
 *    assets/component-buy-buttons-v2.js, using this element's own
 *    data-money-format attribute. Swaps the card's image to data-variant-image (a
 *    prerendered image_url, same gallery-image/featured-image fallback
 *    snippets/living-room-upsell-v2.liquid uses for the initial render —
 *    see that file's {% doc %}), and updates that card's ADD button's
 *    data-variant-id so the add-to-cart payload always reflects the
 *    selected shape. No server round-trip: the coffee-table product's
 *    variants (and their prices/images) are already fully present in the
 *    DOM via each radio's data attributes, mirroring how
 *    assets/component-configuration-picker-v2.js resolves variant
 *    changes from a prerendered map rather than a fetch.
 *
 * 2. ADD buttons (data-lru-add) — POST a single-item payload to
 *    /cart/add.js and refresh the cart drawer, reusing the exact
 *    sections/renderContents pattern documented in
 *    assets/component-buy-buttons-v2.js's _submitWithAddons: same
 *    fetchConfig('javascript') call, same cart.getSectionsToRender() +
 *    sections_url body fields, same cart.renderContents(response)
 *    refresh / cart.setActiveElement focus-return call, same
 *    PUB_SUB_EVENTS.cartUpdate publish. Falls back to a full-page
 *    navigation to the cart when no cart-drawer/cart-notification
 *    element is present, matching that file's own fallback.
 */
(function () {
  // Idempotency guard, same pattern as component-buy-buttons-v2.js and
  // component-modules-addons-v2.js.
  if (window.__v2LivingRoomUpsellBound) return;
  window.__v2LivingRoomUpsellBound = true;

  if (customElements.get('living-room-upsell-v2')) return;

  // Byte-consistent duplication of the 3-tier money formatter from
  // assets/component-buy-buttons-v2.js (see that file's doc comment for
  // the full precedence rationale: Shopify.formatMoney with the shop's
  // real money_format string first, then a local money_format
  // interpreter, then window.formatMoney as a last resort).
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents == null) return '0';
    var number = (cents / 100.0).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var centsPart = '';
    if (parts[1]) centsPart = decimal + parts[1];
    return dollars + centsPart;
  }

  function formatWithMoneyFormat(cents, format) {
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholderRegex);
    var value;
    var placeholder = 'amount';
    if (match) placeholder = match[1];
    switch (placeholder) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }
    return format.replace(placeholderRegex, value).replace(/<[^>]*>/g, '').trim();
  }

  function formatMoneyCents(cents, format) {
    if (format) {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, format);
      }
      return formatWithMoneyFormat(cents, format);
    }
    if (typeof window.formatMoney === 'function') {
      return '$' + window.formatMoney(cents);
    }
    return '$' + (Math.round(cents) / 100).toFixed(0);
  }

  customElements.define(
    'living-room-upsell-v2',
    class extends HTMLElement {
      connectedCallback() {
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this._onChange = this._onChange.bind(this);
        this._onClick = this._onClick.bind(this);
        this.addEventListener('change', this._onChange);
        this.addEventListener('click', this._onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this._onChange);
        this.removeEventListener('click', this._onClick);
      }

      _onChange(event) {
        // Cushion colour swatches: update the selected variant, price (colours
        // differ in price) and swatch selected state.
        var colourInput = event.target.closest('[data-lru-colour-input]');
        if (colourInput && this.contains(colourInput)) {
          this._selectColour(colourInput);
          return;
        }

        var input = event.target.closest('[data-lru-shape-input]');
        if (!input || !this.contains(input)) return;

        var card = input.closest('[data-lru-card]');
        if (!card) return;

        // data-combined-price-cents mirrors the exact tray+discount math
        // used for this card's initial SSR price (see
        // snippets/living-room-upsell-v2.liquid's {% doc %}) — the bare
        // variant price alone would drop the tray/discount combo.
        var priceCents = parseInt(input.dataset.combinedPriceCents, 10) || 0;
        // data-compare-cents mirrors the exact three-way SSR strikethrough
        // branch for THIS variant (see snippets/living-room-upsell-v2.liquid's
        // {% doc %}): pre-discount combined total when a discount applies,
        // else the native compare_at + tray compare_at sum when that exceeds
        // the combined price, else 0/absent. A single attribute keeps the
        // toggle-time strikethrough identical to a fresh SSR render for
        // every discount/compare-at configuration.
        var compareCents = parseInt(input.dataset.compareCents, 10) || 0;
        var moneyFormat = this.dataset.moneyFormat;

        var priceEl = card.querySelector('[data-lru-price]');
        if (priceEl) {
          // Sale price on top, struck compare-at below — the price container
          // is a flex-column (see component-living-room-upsell-v2.css), so
          // DOM order is visual top-to-bottom, matching the SSR markup.
          var html = '<span class="v2-price-sale">' + formatMoneyCents(priceCents, moneyFormat) + '</span>';
          if (compareCents > priceCents) {
            html += '<span class="v2-price-compare">' + formatMoneyCents(compareCents, moneyFormat) + '</span>';
          }
          priceEl.innerHTML = html;
        }

        var addButton = card.querySelector('[data-lru-add]');
        if (addButton) addButton.dataset.variantId = input.value;

        var imageUrl = input.dataset.variantImage;
        if (imageUrl) {
          var imageEl = card.querySelector('[data-lru-image] img');
          // Clear srcset — Shopify's image_tag emits one, and the browser renders
          // from srcset over src, so updating src alone wouldn't change the image.
          if (imageEl) { imageEl.removeAttribute('srcset'); imageEl.removeAttribute('sizes'); imageEl.src = imageUrl; }
        }

        card.querySelectorAll('.lru-v2__tile').forEach(function (tile) {
          var tileInput = tile.querySelector('[data-lru-shape-input]');
          var selected = tileInput === input;
          tile.classList.toggle('v2-tile--selected', selected);
        });
      }

      _selectColour(input) {
        var card = input.closest('[data-lru-card]');
        if (!card) return;

        var priceCents = parseInt(input.dataset.priceCents, 10) || 0;
        var compareCents = parseInt(input.dataset.compareCents, 10) || 0;
        var moneyFormat = this.dataset.moneyFormat;

        var priceEl = card.querySelector('[data-lru-price]');
        if (priceEl) {
          var html = '<span class="v2-price-sale">' + formatMoneyCents(priceCents, moneyFormat) + '</span>';
          if (compareCents > priceCents) {
            html += '<span class="v2-price-compare">' + formatMoneyCents(compareCents, moneyFormat) + '</span>';
          }
          priceEl.innerHTML = html;
        }

        var addButton = card.querySelector('[data-lru-add]');
        if (addButton) addButton.dataset.variantId = input.value;

        // Swap the card image to the selected colour's photo (mirrors the shape
        // handler). No visible change until the cushion variants carry distinct
        // per-colour images — until then every colour falls back to the same
        // product photo.
        var imageUrl = input.dataset.variantImage;
        if (imageUrl) {
          var imageEl = card.querySelector('[data-lru-image] img');
          // Clear srcset — Shopify's image_tag emits one, and the browser renders
          // from srcset over src, so updating src alone wouldn't change the image.
          if (imageEl) { imageEl.removeAttribute('srcset'); imageEl.removeAttribute('sizes'); imageEl.src = imageUrl; }
        }

        card.querySelectorAll('.lru-v2__swatch').forEach(function (swatch) {
          var swatchInput = swatch.querySelector('[data-lru-colour-input]');
          swatch.classList.toggle('lru-v2__swatch--selected', swatchInput === input);
        });
      }

      _onClick(event) {
        var button = event.target.closest('[data-lru-add]');
        if (!button || !this.contains(button) || button.disabled) return;

        var variantId = button.dataset.variantId;
        if (!variantId) return;

        this._addToCart(variantId, button);
      }

      _addToCart(variantId, button) {
        var body = { items: [{ id: parseInt(variantId, 10), quantity: 1 }] };

        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          body.sections = this.cart.getSectionsToRender().map(function (section) {
            return section.id;
          });
          body.sections_url = window.location.pathname;
          if (typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(document.activeElement);
          }
        }

        button.classList.add('loading');
        button.disabled = true;

        var config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);

        var self = this;
        fetch(window.routes.cart_add_url, config)
          .then(function (response) {
            return response.json();
          })
          .then(function (response) {
            if (response.status) {
              console.error(response.description || response.message);
              return;
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'living-room-upsell-v2', productVariantId: variantId });
            }

            if (self.cart && typeof self.cart.renderContents === 'function') {
              self.cart.renderContents(response);
            } else {
              window.location = window.routes.cart_url;
            }
          })
          .catch(function (e) {
            console.error(e);
          })
          .finally(function () {
            button.classList.remove('loading');
            button.disabled = false;
          });
      }
    }
  );
})();
